import React from "react";
import { EMPTY_MAP_BUY_BOX, MapBuyBox, mapBuyBoxActiveCount } from "../lib/mapBuyBox";
import MapSavedBuyBoxes from "./MapSavedBuyBoxes";

// The map tab's buy box fields (#3061), rendered inside the filter bar's
// "More" popover. Matching rules, and why missing data is kept by default,
// live in lib/mapBuyBox.ts.

export interface MapBuyBoxStats {
  missingKept: number;
  missingExcluded: number;
  outsideExcluded: number;
}

interface Props {
  value: MapBuyBox;
  onChange: (next: MapBuyBox) => void;
  matchCount: number;
  stats: MapBuyBoxStats;
  /** Load and save the Leads tab's saved buy boxes here. Paid plans only; omit to hide. */
  saved?: MapBuyBoxSavedConfig;
}

export interface MapBuyBoxSavedConfig {
  countyId: string;
  countyName?: string;
  savedLimit?: number;
}

// Empty or invalid input means "no bound", never 0.
function parseNum(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}

const fmt = (n: number) => n.toLocaleString("en-US");

type NumKey = Exclude<keyof MapBuyBox, "includeUnknown">;

const MapBuyBoxFields: React.FC<Props> = ({ value, onChange, matchCount, stats, saved }) => {
  const set = (key: NumKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: parseNum(e.target.value) });

  const input = (key: NumKey, label: string, placeholder: string) => (
    <input
      type="number"
      inputMode="numeric"
      min={0}
      className="lv-chip bbx-num"
      aria-label={label}
      placeholder={placeholder}
      value={value[key] ?? ""}
      onChange={set(key)}
    />
  );

  const active = mapBuyBoxActiveCount(value) > 0;

  return (
    <fieldset className="map-buybox" aria-label="Buy box">
      <legend className="map-buybox-title">Buy box</legend>

      {saved && (
        <MapSavedBuyBoxes
          current={value}
          countyId={saved.countyId}
          countyName={saved.countyName}
          savedLimit={saved.savedLimit}
          onApply={onChange}
        />
      )}

      <div className="map-buybox-row">
        <span className="map-buybox-label">Sq ft</span>
        {input("minSqft", "Minimum square feet", "Min")}
        <span aria-hidden="true">–</span>
        {input("maxSqft", "Maximum square feet", "Max")}
      </div>
      <div className="map-buybox-row">
        <span className="map-buybox-label">Year built</span>
        {input("minYearBuilt", "Built in or after", "From")}
        <span aria-hidden="true">–</span>
        {input("maxYearBuilt", "Built in or before", "To")}
      </div>
      <div className="map-buybox-row">
        <span className="map-buybox-label">Beds / baths</span>
        {input("minBeds", "Minimum bedrooms", "Beds")}
        <span aria-hidden="true">+</span>
        {input("minBaths", "Minimum bathrooms", "Baths")}
      </div>
      <div className="map-buybox-row">
        <span className="map-buybox-label">Value</span>
        {input("minValue", "Minimum assessed value", "Min $")}
        <span aria-hidden="true">–</span>
        {input("maxValue", "Maximum assessed value", "Max $")}
      </div>
      <div className="map-buybox-row">
        <span className="map-buybox-label">Min equity</span>
        {input("minEquity", "Minimum equity in dollars", "$")}
      </div>

      <div className="map-buybox-row">
        <span className="map-buybox-label">Owed</span>
        {input("minOwed", "Minimum amount owed", "Min $")}
        <span aria-hidden="true">–</span>
        {input("maxOwed", "Maximum amount owed", "Max $")}
      </div>
      <div className="map-buybox-row">
        <span className="map-buybox-label">Acres</span>
        {input("minAcres", "Minimum acres", "Min")}
        <span aria-hidden="true">–</span>
        {input("maxAcres", "Maximum acres", "Max")}
      </div>
      <div className="map-buybox-row">
        <span className="map-buybox-label">Land value</span>
        {input("minLandValue", "Minimum land value", "Min $")}
        <span aria-hidden="true">–</span>
        {input("maxLandValue", "Maximum land value", "Max $")}
      </div>

      <label className="filter-row-checkbox" title="Many counties don't publish every building fact">
        <input
          type="checkbox"
          checked={value.includeUnknown}
          onChange={(e) => onChange({ ...value, includeUnknown: e.target.checked })}
        />
        <span>Include properties with missing data</span>
      </label>

      {active && (
        <p className="map-buybox-summary" role="status">
          <strong>{fmt(matchCount)}</strong> match
          {stats.missingKept > 0 && <> · {fmt(stats.missingKept)} of them have missing data</>}
          {stats.missingExcluded > 0 && <> · {fmt(stats.missingExcluded)} hidden for missing data</>}
          {stats.outsideExcluded > 0 && <> · {fmt(stats.outsideExcluded)} outside your plan's counties not shown</>}
        </p>
      )}

      {active && (
        <button type="button" className="map-buybox-clear" onClick={() => onChange(EMPTY_MAP_BUY_BOX)}>
          Clear buy box
        </button>
      )}
    </fieldset>
  );
};

export default MapBuyBoxFields;
